// ABOUTME: Main React application component with routing and context providers.
// ABOUTME: Configures theme, authentication, shop selection, and page routing.

import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ShopProvider, useShop } from './contexts/ShopContext';
import { DashboardProvider } from './contexts/DashboardContext';
import Layout from './components/Layout';
import ErrorBoundary from './components/ErrorBoundary';
import Dashboard from './pages/Dashboard';
import ConfigPage from './pages/ConfigPage';
import Login from './components/Login';
import ShopSelector from './components/ShopSelector';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import './i18n';

const theme = createTheme({
  palette: {
    primary: {
      main: '#8B4513',
    },
    secondary: {
      main: '#FFA500',
    },
    background: {
      default: '#f5f5f5',
    },
  },
  typography: {
    fontFamily: 'Roboto, Arial, sans-serif',
  },
});

// Component that redirects to the last selected shop (if any)
function RedirectToLastShop() {
  const { currentShopId, shops, switchShop, loading } = useShop();

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!currentShopId) {
    // No shop selected - show shop selector modal
    return <ShopSelector shops={shops} onSelectShop={switchShop} />;
  }

  return <Navigate to={`/shops/${currentShopId}/dashboard`} replace />;
}

// Wrapper component that extracts shopId from URL params
function ShopDashboard() {
  const { shopId } = useParams();
  return <Dashboard shopId={shopId} />;
}

function AppContent() {
  const { isAuthenticated, loading: authLoading, login } = useAuth();

  if (authLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh'
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!isAuthenticated) {
    return <Login onLoginSuccess={login} />;
  }

  return (
    <ShopProvider>
      <DashboardProvider>
        <Router>
          <Layout>
            <ErrorBoundary>
              <Routes>
                <Route path="/shops/:shopId/dashboard" element={<ShopDashboard />} />
                <Route path="/config" element={<ConfigPage />} />
                <Route path="/" element={<RedirectToLastShop />} />
                <Route path="*" element={<RedirectToLastShop />} />
              </Routes>
            </ErrorBoundary>
          </Layout>
        </Router>
      </DashboardProvider>
    </ShopProvider>
  );
}

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;